<script setup lang="ts">
import type { AuniversMetadata } from '~/models/Product'
import type { BaseItem } from '~/models/Content/BaseItem'
import type { Task } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { faArrowRight, faLock } from '@fortawesome/duotone-regular-svg-icons'
import useProductStore from '~/stores/product'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import { useStaticProduct } from '~/composables/useStaticProduct'
import useProductHelper from '~/composables/useProductHelper'
import useGradeString from '~/composables/useGradeString'
import useDescriptor from '~/composables/useDescriptor'
import LinkOrAnchor from '~/components/utils/LinkOrAnchor.vue'
import LockedContentDialog from '~/components/search/LockedContentDialog.vue'
import CardMenu from '~/components/cards/buttons/CardMenu.vue'
import SharedCardBadge from '~/components/cards/badges/SharedCardBadge.vue'

const { item, packageStyle = false, showPath = true, showThumbnail = true } = defineProps<{
  item: BaseItem
  task?: Task
  packageStyle?: boolean
  showPath?: boolean
  showThumbnail?: boolean
}>()

const { t } = useI18n()
const { getDescriptor } = useDescriptor()
const { buildResourceUrl } = useUrlBuilder()
const { createGradeString } = useGradeString()
const { getProductFromResource } = useProductStore()
const { products } = storeToRefs(useProductStore())
const { isAddonProduct } = useProductHelper()
const { ALWAYS_SEARCHABLE_PRODUCTS } = useStaticProduct()

const dialogOpen = ref(false)

const matchesProduct = (metadata: AuniversMetadata) => metadata.pathString && item.pathString.includes(metadata.pathString)

const belongsToAlways = computed(() => ALWAYS_SEARCHABLE_PRODUCTS.find(({ aunivers }) => matchesProduct(aunivers)))
const belongsToProduct = computed(() => products.value.find(({ aunivers }) => matchesProduct(aunivers)))

const hasAccess = computed(() => !(belongsToAlways.value && !belongsToProduct.value))
const product = computed(() => getProductFromResource(item))
const link = computed(() => buildResourceUrl(item))
const label = computed(() => getDescriptor(item))

const subjectOrProductName = computed(() => {
  if (!hasAccess.value)
    return (belongsToProduct.value || belongsToAlways.value)?.name
  if (!product.value)
    return ''
  if (isAddonProduct(product.value))
    return product.value.aunivers?.name
  return product.value.subjects?.length ? t(`metadata.subjects.${product.value.subjects[0]}`) : ''
})
const subtitle = computed(() => {
  const subject = subjectOrProductName.value
  const grades = createGradeString(item.grades)
  const parent = item.parentLocationName

  return [subject, grades, parent].filter(Boolean).join(' / ')
})
</script>

<template>
  <li
    class="group relative size-full rounded-xl bg-white transition hover:shadow-lg has-focus-visible:ring"
    :class="packageStyle ? 'grid grid-cols-1' : 'grid grid-cols-3 gap-1 border border-gray-10'"
  >
    <SharedCardBadge v-if="task?.homework" />
    <div
      class="col-span-2 flex flex-col p-3"
      :class="item.thumbnail ? 'col-span-2' : 'col-span-3'"
    >
      <span
        class="mb-1 font-extrabold text-gray-50"
        :class="packageStyle ? 'text-xl' : 'text-base'"
      >
        <LinkOrAnchor
          v-if="hasAccess"
          class="line-clamp-3 outline-0 before:absolute before:inset-0 before:rounded-md"
          :link="link"
        >
          {{ item.title }}
        </LinkOrAnchor>
        <button
          v-else
          class="line-clamp-3 text-left outline-0 before:absolute before:inset-0 before:rounded-md"
          @click="dialogOpen = true"
        >
          {{ item.title }}
        </button>
      </span>
      <div
        v-if="!hasAccess"
        class="absolute -top-1 -right-1 z-10 flex size-9 cursor-pointer items-center justify-center rounded-full border border-white bg-blue-50 text-white shadow-md"
      >
        <font-awesome-icon :icon="faLock" />
        <LockedContentDialog
          :open="dialogOpen"
          :product="belongsToProduct || belongsToAlways"
          @close-dialog="dialogOpen = false"
        />
      </div>
      <p
        v-if="!!subtitle && showPath"
        class="mb-2 text-xs text-gray-50"
        v-text="subtitle"
      />
      <div v-if="label || item.tiered" class="order-first mb-2 flex items-center gap-1">
        <span
          v-if="label"
          class="bg-seagreen-60 text-seagreen-5 rounded-md px-2 py-1 text-xs font-semibold"
          v-text="label"
        />
        <span
          v-if="item.tiered"
          class="bg-seagreen-60 text-seagreen-5 rounded-md px-2 py-1 text-xs font-semibold"
          v-text="t('search.checkboxes.tiered')"
        />
      </div>

      <CardMenu
        v-if="!packageStyle"
        button-classes="mt-auto z-10"
        :resource="item"
        toggle-shade="dark"
      >
        <template #favorite>
          <slot name="favorite-menu" />
        </template>
      </CardMenu>
      <font-awesome-icon
        v-else
        class="mt-auto transition-all group-hover:ml-2"
        :icon="faArrowRight"
      />
    </div>
    <div
      v-if="item.thumbnail && showThumbnail"
      class="col-span-1 size-full overflow-hidden p-2 transition-all group-hover:p-1.5"
      :class="packageStyle ? 'aspect-video order-first' : 'aspect-square rounded-r-md'"
    >
      <img
        :src="item.thumbnail"
        :alt="item.thumbnailAlt"
        class="size-full rounded-md object-cover"
      >
    </div>
  </li>
</template>
